import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Alert, Stack, Chip,
  FormControlLabel, Switch, Divider, CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  getCurrentUser, updateMyProfile,
  getMyImapSettings, updateMyImapSettings, testMyImapSettings,
} from '../services/api';
import { useAuth } from '../store/AuthContext';

const DEFAULT_IMAP = {
  enabled: false,
  host: 'imap.hostinger.com',
  port: 993,
  useSsl: true,
  username: '',
  password: '',
  folder: 'INBOX',
  intervalSeconds: 180,
  batchSize: 50,
  deleteAfterFetch: false,
  includeAlreadyRead: false,
  sinceDays: 7,
};

const parseLanguages = (raw) =>
  (raw || '').split(',').map(s => s.trim()).filter(Boolean);

export default function MyAccountPage() {
  const { updateUser } = useAuth();
  const [form, setForm] = useState({ fullName: '', jobTitle: '', emailSignature: '', preferredLanguages: '' });
  const [languages, setLanguages] = useState([]);
  const [langInput, setLangInput] = useState('');
  const [meta, setMeta] = useState({ username: '', email: '', role: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [imap, setImap] = useState(DEFAULT_IMAP);
  const [imapLoading, setImapLoading] = useState(true);
  const [imapSaving, setImapSaving] = useState(false);
  const [imapTesting, setImapTesting] = useState(false);
  const [imapMessage, setImapMessage] = useState(null); // {severity, text}

  useEffect(() => {
    getCurrentUser()
      .then(({ data }) => {
        setForm({
          fullName: data.fullName || '',
          jobTitle: data.jobTitle || '',
          emailSignature: data.emailSignature || '',
          preferredLanguages: data.preferredLanguages || '',
        });
        setLanguages(parseLanguages(data.preferredLanguages));
        setMeta({ username: data.username, email: data.email, role: data.role });
      })
      .catch(() => setError('Failed to load profile'))
      .finally(() => setLoading(false));

    getMyImapSettings()
      .then(({ data }) => setImap({ ...DEFAULT_IMAP, ...data }))
      .catch(() => { /* ignore — leave defaults */ })
      .finally(() => setImapLoading(false));
  }, []);

  const updateImap = (patch) => setImap((prev) => ({ ...prev, ...patch }));

  const handleSaveImap = async () => {
    setImapMessage(null);
    setImapSaving(true);
    try {
      const { data } = await updateMyImapSettings(imap);
      setImap({ ...DEFAULT_IMAP, ...data });
      setImapMessage({ severity: 'success', text: 'IMAP settings saved.' });
    } catch (err) {
      setImapMessage({ severity: 'error', text: err.response?.data?.error || err.response?.data?.message || 'Failed to save IMAP settings' });
    } finally {
      setImapSaving(false);
    }
  };

  const handleTestImap = async () => {
    setImapMessage(null);
    setImapTesting(true);
    try {
      const { data } = await testMyImapSettings(imap);
      if (data?.success) {
        setImapMessage({
          severity: 'success',
          text: `Connected to ${data.folderName || 'INBOX'}. Unseen messages: ${data.unseenCount}.`,
        });
      } else {
        setImapMessage({ severity: 'error', text: `Connection failed: ${data?.error || 'unknown error'}` });
      }
    } catch (err) {
      setImapMessage({ severity: 'error', text: err.response?.data?.error || err.response?.data?.message || 'Connection test failed' });
    } finally {
      setImapTesting(false);
    }
  };

  const addLanguage = () => {
    const v = langInput.trim();
    if (!v) return;
    if (languages.some(l => l.toLowerCase() === v.toLowerCase())) {
      setLangInput('');
      return;
    }
    setLanguages([...languages, v]);
    setLangInput('');
  };

  const removeLanguage = (lang) => {
    setLanguages(languages.filter(l => l !== lang));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const payload = { ...form, preferredLanguages: languages.join(', ') };
      const { data } = await updateMyProfile(payload);
      updateUser({
        fullName: data.fullName,
        jobTitle: data.jobTitle,
        emailSignature: data.emailSignature,
        preferredLanguages: data.preferredLanguages,
      });
      setSuccess('Profile saved');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 5 }}><Typography>Loading...</Typography></Box>;

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>My Account</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper sx={{ p: 3, maxWidth: 800 }}>
        <form onSubmit={handleSave}>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <TextField label="Username" size="small" fullWidth value={meta.username} disabled />
            <TextField label="Email" size="small" fullWidth value={meta.email} disabled />
          </Stack>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="Full Name (used as {{SenderName}})"
              size="small"
              fullWidth
              value={form.fullName}
              onChange={e => setForm({ ...form, fullName: e.target.value })}
            />
            <TextField
              label="Job Title (used as {{SenderTitle}})"
              size="small"
              fullWidth
              value={form.jobTitle}
              onChange={e => setForm({ ...form, jobTitle: e.target.value })}
              placeholder="e.g. Sales Manager"
            />
          </Stack>

          <Typography variant="body2" sx={{ fontWeight: 600, mt: 2, mb: 0.5 }}>
            Email Signature
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Substituted as {'{{SenderSignature}}'} in templates. Auto-appended to outgoing emails when not present in body. HTML or plain text.
          </Typography>
          <TextField
            multiline
            rows={10}
            fullWidth
            value={form.emailSignature}
            onChange={e => setForm({ ...form, emailSignature: e.target.value })}
            placeholder={'<p>--<br>Jane Doe<br>Sales Manager<br>jane@company.com</p>'}
            slotProps={{ input: { sx: { fontFamily: 'monospace', fontSize: '0.85rem' } } }}
          />

          {form.emailSignature && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Preview</Typography>
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fff' }}>
                <Box dangerouslySetInnerHTML={{ __html: form.emailSignature }} />
              </Paper>
            </Box>
          )}

          <Typography variant="body2" sx={{ fontWeight: 600, mt: 3, mb: 0.5 }}>
            Preferred Languages for Translation
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Quick-pick chips on Compose Email → Translate Preview. Add full names ("French", "Hebrew", "Spanish") or codes ("he", "ar").
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}>
            {languages.map(l => (
              <Chip key={l} label={l} onDelete={() => removeLanguage(l)} size="small" />
            ))}
            {languages.length === 0 && (
              <Typography variant="caption" color="text.secondary">No languages added yet.</Typography>
            )}
          </Stack>
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              value={langInput}
              onChange={e => setLangInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addLanguage(); } }}
              placeholder="e.g. French, Hebrew, Spanish"
              sx={{ minWidth: 240 }}
            />
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addLanguage}>
              Add
            </Button>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Stack>
        </form>
      </Paper>

      <Paper sx={{ p: 3, maxWidth: 800, mt: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Inbox (IMAP)</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Connect your Hostinger (or any IMAP) mailbox so replies are pulled into the Inbox and linked to leads.
          Password is stored on your profile and used only by the inbox poller.
        </Typography>

        {imapMessage && (
          <Alert severity={imapMessage.severity} sx={{ mb: 2 }} onClose={() => setImapMessage(null)}>
            {imapMessage.text}
          </Alert>
        )}

        {imapLoading ? (
          <Stack direction="row" alignItems="center" spacing={1}><CircularProgress size={16} /><Typography variant="body2">Loading…</Typography></Stack>
        ) : (
          <>
            <FormControlLabel
              control={<Switch checked={!!imap.enabled} onChange={(e) => updateImap({ enabled: e.target.checked })} />}
              label="Enable IMAP polling for my account"
              sx={{ mb: 1 }}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
              <TextField
                label="IMAP Host" size="small" fullWidth
                value={imap.host}
                onChange={(e) => updateImap({ host: e.target.value })}
                placeholder="imap.hostinger.com"
              />
              <TextField
                label="Port" size="small" type="number"
                value={imap.port}
                onChange={(e) => updateImap({ port: parseInt(e.target.value, 10) || 993 })}
                sx={{ width: 120 }}
              />
              <FormControlLabel
                control={<Switch checked={!!imap.useSsl} onChange={(e) => updateImap({ useSsl: e.target.checked })} />}
                label="SSL"
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
              <TextField
                label="Username (mailbox)" size="small" fullWidth
                value={imap.username}
                onChange={(e) => updateImap({ username: e.target.value })}
                placeholder="replies@yourdomain.com"
                autoComplete="off"
              />
              <TextField
                label="Password" size="small" type="password" fullWidth
                value={imap.password}
                onChange={(e) => updateImap({ password: e.target.value })}
                placeholder="(leave blank to keep stored password)"
                autoComplete="new-password"
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
              <TextField
                label="Folder" size="small"
                value={imap.folder}
                onChange={(e) => updateImap({ folder: e.target.value })}
                sx={{ width: 200 }}
              />
              <TextField
                label="Poll interval (seconds)" size="small" type="number"
                value={imap.intervalSeconds}
                onChange={(e) => updateImap({ intervalSeconds: parseInt(e.target.value, 10) || 180 })}
                sx={{ width: 200 }}
                helperText="Min 30, max 3600"
              />
              <TextField
                label="Batch size" size="small" type="number"
                value={imap.batchSize}
                onChange={(e) => updateImap({ batchSize: parseInt(e.target.value, 10) || 50 })}
                sx={{ width: 140 }}
                helperText="Max 200 / tick"
              />
            </Stack>

            <FormControlLabel
              control={<Switch checked={!!imap.deleteAfterFetch} onChange={(e) => updateImap({ deleteAfterFetch: e.target.checked })} />}
              label="Delete messages on the server after fetch"
              sx={{ mb: 1 }}
            />

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Read-elsewhere handling</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Turn this on if you also read this mailbox in Outlook / Gmail / phone. The poller will fetch every
              message in the look-back window regardless of read state, and won't touch the server's read flags.
              Duplicates are filtered by Message-ID.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
              <FormControlLabel
                control={<Switch checked={!!imap.includeAlreadyRead} onChange={(e) => updateImap({ includeAlreadyRead: e.target.checked })} />}
                label="Import already-read messages too"
              />
              <TextField
                label="Look-back window (days)" size="small" type="number"
                value={imap.sinceDays}
                onChange={(e) => updateImap({ sinceDays: parseInt(e.target.value, 10) || 7 })}
                disabled={!imap.includeAlreadyRead}
                sx={{ width: 200 }}
                helperText="1–90"
              />
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={handleSaveImap} disabled={imapSaving || imapTesting}>
                {imapSaving ? 'Saving…' : 'Save IMAP settings'}
              </Button>
              <Button variant="outlined" onClick={handleTestImap} disabled={imapSaving || imapTesting}>
                {imapTesting ? 'Testing…' : 'Test connection'}
              </Button>
            </Stack>
          </>
        )}
      </Paper>
    </Box>
  );
}
